import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";

const emptyProduct = {
  slug: "",
  name: "",
  tagline: "",
  short_description: "",
  composition: [{ name: "", value: "" }],
  benefits: [""],
  packaging: "",
  pack_details: [""],
  flavour: "",
  image: "",
  brochure: "",
  category: "Pediatrics",
  color_theme: "#0055A4",
};

export const ProductFormDialog = ({ open, onOpenChange, initial, onSubmit, submitting }) => {
  const [data, setData] = useState(emptyProduct);

  useEffect(() => {
    if (open) {
      setData(
        initial
          ? {
              ...initial,
              flavour: initial.flavour ?? "",
              composition: initial.composition?.length
                ? initial.composition
                : [{ name: "", value: "" }],
              benefits: initial.benefits?.length ? initial.benefits : [""],
              pack_details: initial.pack_details?.length ? initial.pack_details : [""],
            }
          : emptyProduct
      );
    }
  }, [open, initial]);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const updateList = (key, idx, val) =>
    setData((d) => {
      const arr = [...d[key]];
      arr[idx] = val;
      return { ...d, [key]: arr };
    });

  const updateComposition = (idx, k, val) =>
    setData((d) => {
      const arr = [...d.composition];
      arr[idx] = { ...arr[idx], [k]: val };
      return { ...d, composition: arr };
    });

  const addItem = (key, fresh) =>
    setData((d) => ({ ...d, [key]: [...d[key], fresh] }));

  const removeItem = (key, idx) =>
    setData((d) => ({ ...d, [key]: d[key].filter((_, i) => i !== idx) }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...data,
      flavour: data.flavour?.trim() || null,
      composition: data.composition.filter((c) => c.name.trim()),
      benefits: data.benefits.map((b) => b.trim()).filter(Boolean),
      pack_details: data.pack_details.map((p) => p.trim()).filter(Boolean),
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="product-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>{initial ? "Edit product" : "Add new product"}</DialogTitle>
          <DialogDescription>
            All fields marked * are required. Image and Brochure should be URLs.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold">Name *</Label>
              <Input
                required
                value={data.name}
                onChange={(e) => setField("name", e.target.value)}
                className="mt-2 h-10 rounded-lg"
                data-testid="product-form-name"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Slug *</Label>
              <Input
                required
                value={data.slug}
                onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                className="mt-2 h-10 rounded-lg"
                placeholder="my-product"
                data-testid="product-form-slug"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold">Tagline *</Label>
            <Input
              required
              value={data.tagline}
              onChange={(e) => setField("tagline", e.target.value)}
              className="mt-2 h-10 rounded-lg"
              data-testid="product-form-tagline"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold">Short Description *</Label>
            <Textarea
              required
              rows={3}
              value={data.short_description}
              onChange={(e) => setField("short_description", e.target.value)}
              className="mt-2 rounded-lg"
              data-testid="product-form-description"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold">Product Image *</Label>
              <div className="mt-2">
                <ImageUpload
                  value={data.image}
                  onChange={(v) => setField("image", v)}
                  accept="image/*"
                  testId="product-form-image"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold">Brochure (image or PDF) *</Label>
              <div className="mt-2">
                <ImageUpload
                  value={data.brochure}
                  onChange={(v) => setField("brochure", v)}
                  accept="image/*,application/pdf"
                  testId="product-form-brochure"
                />
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-semibold">Category</Label>
              <Input
                value={data.category}
                onChange={(e) => setField("category", e.target.value)}
                className="mt-2 h-10 rounded-lg"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Flavour</Label>
              <Input
                value={data.flavour}
                onChange={(e) => setField("flavour", e.target.value)}
                className="mt-2 h-10 rounded-lg"
                placeholder="(optional)"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Theme color</Label>
              <Input
                type="color"
                value={data.color_theme}
                onChange={(e) => setField("color_theme", e.target.value)}
                className="mt-2 h-10 rounded-lg p-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold">Packaging *</Label>
            <Input
              required
              value={data.packaging}
              onChange={(e) => setField("packaging", e.target.value)}
              className="mt-2 h-10 rounded-lg"
              data-testid="product-form-packaging"
            />
          </div>

          {/* Composition list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Composition *</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => addItem("composition", { name: "", value: "" })}
                className="rounded-full h-8 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" /> Row
              </Button>
            </div>
            <div className="space-y-2">
              {data.composition.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2">
                  <Input
                    value={c.name}
                    onChange={(e) => updateComposition(i, "name", e.target.value)}
                    placeholder="Ingredient"
                    className="h-9 rounded-lg"
                  />
                  <Input
                    value={c.value}
                    onChange={(e) => updateComposition(i, "value", e.target.value)}
                    placeholder="e.g. 400 IU"
                    className="h-9 rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem("composition", i)}
                    className="h-9 w-9 p-0 text-destructive"
                    disabled={data.composition.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Benefits *</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => addItem("benefits", "")}
                className="rounded-full h-8 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" /> Item
              </Button>
            </div>
            <div className="space-y-2">
              {data.benefits.map((b, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={b}
                    onChange={(e) => updateList("benefits", i, e.target.value)}
                    placeholder="e.g. Boosts immunity"
                    className="h-9 rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem("benefits", i)}
                    className="h-9 w-9 p-0 text-destructive"
                    disabled={data.benefits.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Pack details */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Pack Details</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => addItem("pack_details", "")}
                className="rounded-full h-8 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" /> Item
              </Button>
            </div>
            <div className="space-y-2">
              {data.pack_details.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={p}
                    onChange={(e) => updateList("pack_details", i, e.target.value)}
                    placeholder="e.g. Net Volume: 30 ml"
                    className="h-9 rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem("pack_details", i)}
                    className="h-9 w-9 p-0 text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-primary hover:bg-primary/90 px-6"
              data-testid="product-form-submit"
            >
              {submitting ? "Saving…" : initial ? "Update product" : "Create product"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormDialog;
